// THE APP-WIDE WRITE ERROR SURFACE.
//
// Why this exists: "the button does nothing" is this codebase's most expensive recurring bug —
// a Firestore write denied by rules, swallowed by `.catch(() => {})`, leaving the UI showing
// success. An architecture review on 2026-08-15 found 32 write-adjacent swallows across 18 files.
//
// eosWrites.js already proved the fix (its `guarded()` helper: report centrally, still reject).
// This module generalizes that to the whole app so no call site has to learn a new habit — wrap
// the promise, keep the fire-and-forget call shape, and a denial becomes a visible toast instead
// of silence.
//
// USE:
//   guard(deleteDoc(ref), 'delete this item')          // fire-and-forget, now visible
//   await guard(setDoc(ref, data), 'save your changes') // still rejects, callers may still catch
//
// The action phrase completes "You don't have permission to ___." — write it that way.

const listeners = new Set()

// Toast host subscribes; returns an unsubscribe. Kept dependency-free so lib/ imports no React.
export const onWriteError = (fn) => {
  listeners.add(fn)
  return () => listeners.delete(fn)
}

const friendlyFor = (e, action) => {
  const code = e?.code || ''
  // Firestore codes, then Storage's (an upload is a write too; its rules refuse with storage/unauthorized).
  if (code === 'permission-denied' || code === 'storage/unauthorized') return `You don't have permission to ${action}.`
  if (code === 'unavailable' || code === 'deadline-exceeded' || code === 'storage/retry-limit-exceeded') return `Couldn't ${action} — the connection dropped. Check your network and try again.`
  if (code === 'storage/canceled') return `Couldn't ${action} — the upload was cancelled before it finished.`
  if (code === 'storage/quota-exceeded') return `Couldn't ${action} right now — storage is full. Try again later.`
  // The uploader's own refusal (src/lib/uploadDocument.js) already says what to do.
  if (code === 'upload/too-large') return `Couldn't ${action}. ${e.message}`
  if (code === 'not-found') return `Couldn't ${action} — it may have already been removed. Refresh and try again.`
  if (code === 'failed-precondition') return `Couldn't ${action} — something changed since this page loaded. Refresh and try again.`
  if (code === 'resource-exhausted') return `Couldn't ${action} right now — too many requests. Wait a moment and try again.`
  return `Couldn't ${action}. ${e?.message || 'Unexpected error.'}`
}

export const reportWriteError = (e, action) => {
  const message = friendlyFor(e, action)
  // Report each error ONCE. A lib helper may guard a write and its caller may guard the helper; the
  // second report would be a duplicate toast for one failure. The caller still gets the sentence, so
  // `setErr(reportWriteError(e, …))` shows it in place whether or not a helper reported it first.
  if (e && typeof e === 'object') { if (e._writeGuardReported) return message; e._writeGuardReported = true }
  console.error(`[write] ${action} failed:`, e?.code || '', e?.message || e)
  listeners.forEach((fn) => { try { fn({ message, code: e?.code || null, action }) } catch { /* a broken listener must not break the write path */ } })
  return message
}

/**
 * Wrap a Firestore write promise. Reports failures to the app-wide surface and RE-THROWS, so
 * callers that already handle errors keep working unchanged. Never swallows.
 */
export const guard = (promise, action) => Promise.resolve(promise).catch((e) => {
  reportWriteError(e, action)
  throw e
})

/**
 * Same, for the genuine fire-and-forget case: reports, then resolves rather than rejecting, so a
 * failure cannot become an unhandled rejection. Use ONLY where there is no caller to handle it.
 */
export const guardSilentOk = (promise, action) => Promise.resolve(promise).catch((e) => {
  reportWriteError(e, action)
  return null
})
